/**
 * HintCaptureMenu — wraps a region of text and captures analyst hints when
 * the user selects a piece of that text.
 *
 * When the user releases the mouse on a selection inside the wrapped region,
 * a floating menu appears near the selection offering three correction
 * kinds: tag as Module, tag as Entity Type, or tag as Table/Field Binding.
 * Selecting an option opens a dialog that asks for the target value (module
 * name, entity type label, or table+field). Saving the dialog calls the
 * `/issue-analysis/hints` endpoint via `issueAnalysisApi.createHint`.
 *
 * The component is intentionally generic so the same wrapper can be reused
 * over the Description, AI Summary, and Extracted Entities areas.
 */

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { issueAnalysisApi, ModuleHint, ModuleTable } from '../../services/issueAnalysisApi';

type HintKind = 'module' | 'entity_type' | 'entity_value';

interface MenuPosition {
  x: number;
  y: number;
}

interface HintCaptureMenuProps {
  /** The content to wrap. Text selections inside this content trigger the menu. */
  children: ReactNode;
  /** Optional ticket key used to populate `source_ticket` on the saved hint. */
  ticketKey?: string;
  /** Module catalogue, used to populate the module dropdown. */
  availableModules?: string[];
  /** Tables available for the current ticket — used to populate the entity-value
   *  binding pickers. Optional; if missing, the binding option is hidden. */
  availableTables?: ModuleTable[];
  /** Recognised entity-type labels (e.g. "Container", "Work Order", "Agency"). */
  availableEntityTypes?: string[];
  /** Notification callback when a hint saves successfully. */
  onHintSaved?: (hint: ModuleHint) => void;
}

/** Entity-type labels are fetched exclusively from the database.
 *  No hardcoded list — single source of truth. */
const DEFAULT_ENTITY_TYPE_LABELS: string[] = [];

export function HintCaptureMenu({
  children,
  ticketKey,
  availableModules = [],
  availableTables = [],
  availableEntityTypes,
  onHintSaved,
}: HintCaptureMenuProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [selectedText, setSelectedText] = useState<string>('');
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const [openDialog, setOpenDialog] = useState<HintKind | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  // Combined dropdown list: hardcoded labels + analyst-managed custom
  // types from the runtime master. Fetched once on mount; failure is
  // tolerated silently so the hardcoded list still works.
  const [customLabels, setCustomLabels] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    issueAnalysisApi
      .listEntityTypes()
      .then((res) => {
        if (!cancelled) {
          setCustomLabels(res.entity_types.map((t) => t.name));
        }
      })
      .catch(() => {
        // Custom-type loading is non-essential; the hardcoded list still works.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const dropdownEntityTypes = useMemo(() => {
    if (availableEntityTypes && availableEntityTypes.length > 0) {
      return availableEntityTypes;
    }
    const set = new Set<string>([...DEFAULT_ENTITY_TYPE_LABELS, ...customLabels]);
    return Array.from(set).sort();
  }, [availableEntityTypes, customLabels]);

  // Detect text selection inside the wrapper. We listen for mouseup on
  // the document so the selection coordinates are accurate even when the
  // mouse leaves the wrapper mid-drag.
  useEffect(() => {
    function handleMouseUp(event: MouseEvent) {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setMenuPos(null);
        return;
      }
      const text = selection.toString().trim();
      if (text.length === 0 || text.length > 2000) {
        setMenuPos(null);
        return;
      }
      // Only react when the selection's anchor is inside our wrapper.
      const anchor = selection.anchorNode;
      if (!anchor || !wrapperRef.current?.contains(anchor)) {
        return;
      }
      // Prefer the selection rectangle's coordinates over the mouse position
      // so the menu lands consistently next to the selected text even when
      // the user releases the mouse off-target. Fall back to clientX/clientY
      // (viewport-relative, correct for position: fixed) if no rect is
      // available.
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const rect = range?.getBoundingClientRect();
      if (rect && rect.width > 0) {
        setSelectedText(text);
        setMenuPos({ x: rect.left + rect.width / 2, y: rect.bottom });
      } else {
        setSelectedText(text);
        setMenuPos({ x: event.clientX, y: event.clientY });
      }
    }

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuPos(null);
  }, []);

  const openHintDialog = useCallback((kind: HintKind) => {
    setOpenDialog(kind);
    setMenuPos(null);
  }, []);

  const saveHint = useCallback(
    async (
      kind: HintKind,
      fields: { module?: string; entity_type?: string; table_fqn?: string; field_name?: string },
    ) => {
      try {
        const payload: Parameters<typeof issueAnalysisApi.createHint>[0] = {
          hint_type: kind,
          selected_text: selectedText,
          source_ticket: ticketKey,
        };
        if (kind === 'module') {
          if (!fields.module) throw new Error('Module is required.');
          payload.module_name = fields.module;
        } else if (kind === 'entity_type') {
          if (!fields.entity_type) throw new Error('Entity type is required.');
          payload.corrected_entity_type = fields.entity_type;
        } else if (kind === 'entity_value') {
          if (!fields.module || !fields.table_fqn || !fields.field_name) {
            throw new Error('Module, table, and field are all required.');
          }
          payload.module_name = fields.module;
          payload.table_fqn = fields.table_fqn;
          payload.field_name = fields.field_name;
        }
        const saved = await issueAnalysisApi.createHint(payload);
        setStatusMessage(`Correction saved for "${selectedText}". Future tickets will use it automatically.`);
        setOpenDialog(null);
        onHintSaved?.(saved);
        window.setTimeout(() => setStatusMessage(null), 4000);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        // Translate the schema-out-of-date Postgres error into an
        // analyst-friendly message that points to the deployment fix.
        const friendly = message.includes('corrected_entity_type')
          && message.includes('does not exist')
          ? 'Could not save: the backend database is out of date. Ask an admin to redeploy the backend so the v23 migration runs.'
          : `Could not save: ${message}`;
        setStatusMessage(friendly);
        window.setTimeout(() => setStatusMessage(null), 8000);
      }
    },
    [selectedText, ticketKey, onHintSaved],
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {children}

      {menuPos && (
        <FloatingMenu
          x={menuPos.x}
          y={menuPos.y}
          onPickModule={() => openHintDialog('module')}
          onPickEntityType={() => openHintDialog('entity_type')}
          onPickEntityValue={() => openHintDialog('entity_value')}
          onDismiss={closeMenu}
          showEntityValue={availableTables.length > 0}
        />
      )}

      {openDialog === 'module' && (
        <ModuleHintDialog
          selectedText={selectedText}
          modules={availableModules}
          onCancel={() => setOpenDialog(null)}
          onSave={(module) => saveHint('module', { module })}
        />
      )}

      {openDialog === 'entity_type' && (
        <EntityTypeHintDialog
          selectedText={selectedText}
          entityTypes={dropdownEntityTypes}
          onCancel={() => setOpenDialog(null)}
          onSave={(entity_type) => saveHint('entity_type', { entity_type })}
        />
      )}

      {openDialog === 'entity_value' && (
        <EntityValueHintDialog
          selectedText={selectedText}
          tables={availableTables}
          onCancel={() => setOpenDialog(null)}
          onSave={(module, table_fqn, field_name) =>
            saveHint('entity_value', { module, table_fqn, field_name })
          }
        />
      )}

      {statusMessage && (
        <div style={statusStyle}>{statusMessage}</div>
      )}
    </div>
  );
}

// ── Floating menu (appears near the text selection) ──────────────────────

function FloatingMenu(props: {
  x: number;
  y: number;
  onPickModule: () => void;
  onPickEntityType: () => void;
  onPickEntityValue: () => void;
  onDismiss: () => void;
  showEntityValue: boolean;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        left: props.x,
        top: props.y + 8,
        zIndex: 1000,
        background: '#fff',
        border: '1px solid #d0d7de',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
      }}
    >
      <button style={menuItemStyle} onClick={props.onPickModule}>
        Tag as Module
      </button>
      <button style={menuItemStyle} onClick={props.onPickEntityType}>
        Tag as Entity Type
      </button>
      {props.showEntityValue && (
        <button style={menuItemStyle} onClick={props.onPickEntityValue}>
          Tag as Table/Field Binding
        </button>
      )}
      <button style={{ ...menuItemStyle, color: '#888' }} onClick={props.onDismiss}>
        Cancel
      </button>
    </div>
  );
}

// ── Dialog: Module hint ───────────────────────────────────────────────────

function ModuleHintDialog(props: {
  selectedText: string;
  modules: string[];
  onCancel: () => void;
  onSave: (module: string) => void;
}) {
  const [module, setModule] = useState<string>(props.modules[0] ?? '');
  return (
    <DialogShell title="Tag as Module" selectedText={props.selectedText} onCancel={props.onCancel} onSave={() => props.onSave(module)} saveDisabled={!module.trim()}>
      <label style={labelStyle}>
        Module
        <select value={module} onChange={(e) => setModule(e.target.value)} style={selectStyle}>
          <option value="">— Choose —</option>
          {props.modules.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </label>
      <input
        type="text"
        placeholder="Or type a module name not in the list"
        value={props.modules.includes(module) ? '' : module}
        onChange={(e) => setModule(e.target.value)}
        style={inputStyle}
      />
    </DialogShell>
  );
}

// ── Dialog: Entity-type hint ──────────────────────────────────────────────

function EntityTypeHintDialog(props: {
  selectedText: string;
  entityTypes: string[];
  onCancel: () => void;
  onSave: (entityType: string) => void;
}) {
  const [entityType, setEntityType] = useState<string>(props.entityTypes[0] ?? '');
  return (
    <DialogShell title="Tag as Entity Type" selectedText={props.selectedText} onCancel={props.onCancel} onSave={() => props.onSave(entityType)} saveDisabled={!entityType.trim()}>
      <label style={labelStyle}>
        Entity Type
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)} style={selectStyle}>
          {props.entityTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>
    </DialogShell>
  );
}

// ── Dialog: Entity-value (table/field binding) hint ───────────────────────

function EntityValueHintDialog(props: {
  selectedText: string;
  tables: ModuleTable[];
  onCancel: () => void;
  onSave: (module: string, tableFqn: string, fieldName: string) => void;
}) {
  const modules = useMemo(
    () => Array.from(new Set(props.tables.map((t) => t.module))).sort(),
    [props.tables],
  );
  const [module, setModule] = useState<string>(modules[0] ?? '');
  const tablesInModule = useMemo(
    () => props.tables.filter((t) => t.module === module),
    [props.tables, module],
  );
  const [tableFqn, setTableFqn] = useState<string>(tablesInModule[0]?.fqn ?? '');
  const fieldsInTable = useMemo(
    () => tablesInModule.find((t) => t.fqn === tableFqn)?.fields ?? [],
    [tablesInModule, tableFqn],
  );
  const [fieldName, setFieldName] = useState<string>('');

  // Reset downstream pickers when an upstream picker changes.
  useEffect(() => {
    setTableFqn(tablesInModule[0]?.fqn ?? '');
  }, [module, tablesInModule]);
  useEffect(() => {
    setFieldName(fieldsInTable[0] ?? '');
  }, [tableFqn, fieldsInTable]);

  const canSave = module.trim() && tableFqn.trim() && fieldName.trim();
  return (
    <DialogShell title="Tag as Table/Field Binding" selectedText={props.selectedText} onCancel={props.onCancel} onSave={() => canSave && props.onSave(module, tableFqn, fieldName)} saveDisabled={!canSave}>
      <label style={labelStyle}>
        Module
        <select value={module} onChange={(e) => setModule(e.target.value)} style={selectStyle}>
          {modules.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </label>
      <label style={labelStyle}>
        Table
        <select value={tableFqn} onChange={(e) => setTableFqn(e.target.value)} style={selectStyle}>
          {tablesInModule.map((t) => (
            <option key={t.fqn} value={t.fqn}>{t.fqn}</option>
          ))}
        </select>
      </label>
      <label style={labelStyle}>
        Field
        <select value={fieldName} onChange={(e) => setFieldName(e.target.value)} style={selectStyle}>
          {fieldsInTable.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </label>
    </DialogShell>
  );
}

// ── Shared dialog shell ───────────────────────────────────────────────────

function DialogShell(props: {
  title: string;
  selectedText: string;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div style={overlayStyle} onClick={props.onCancel}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px 0' }}>{props.title}</h3>
        <div style={selectedTextBoxStyle}>
          <strong>Selected:</strong> <span>{props.selectedText}</span>
        </div>
        {props.children}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={props.onCancel} style={cancelButtonStyle}>Cancel</button>
          <button onClick={props.onSave} disabled={props.saveDisabled} style={saveButtonStyle}>Save Correction</button>
        </div>
      </div>
    </div>
  );
}

// ── Styles (inline so the component drops in without CSS changes) ─────────

const menuItemStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: 4,
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
};

const dialogStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: 24,
  minWidth: 420,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

const selectedTextBoxStyle: React.CSSProperties = {
  background: '#f6f8fa',
  border: '1px solid #d0d7de',
  borderRadius: 6,
  padding: '8px 12px',
  marginBottom: 16,
  fontSize: 13,
  wordBreak: 'break-word',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 12,
  fontSize: 13,
};

const selectStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '6px 8px',
  border: '1px solid #d0d7de',
  borderRadius: 6,
  fontSize: 13,
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #d0d7de',
  borderRadius: 6,
  fontSize: 13,
};

const saveButtonStyle: React.CSSProperties = {
  background: '#1f6feb',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
};

const cancelButtonStyle: React.CSSProperties = {
  background: '#fff',
  color: '#24292f',
  border: '1px solid #d0d7de',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
};

const statusStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  background: '#0d1117',
  color: '#fff',
  padding: '10px 16px',
  borderRadius: 6,
  fontSize: 13,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  zIndex: 1200,
};
